import React, { memo, useState } from 'react';
import { Button, Form, OverlayTrigger, Pagination, Table, Tooltip } from 'react-bootstrap';
import {
    RiCheckLine,
    RiCloseLine,
    RiDeleteBin2Line,
    RiEditLine,
    RiLockLine,
    RiLockUnlockLine,
    RiErrorWarningLine
} from 'react-icons/ri';
import { Subscriber } from '../../supabase/types';

interface SubscriberTableProps {
    subscribers: Subscriber[];
    onSave: (subscriber: Subscriber, newEmail: string) => void;
    onDelete: (subscriber: Subscriber) => void;
    onActiveToggle: (subscriber: Subscriber) => void;
    currentPage: number;
    totalItems: number;
    itemsPerPage: number;
    onPageChange: (page: number) => void;
}

const SubscriberTable = memo(
    ({ subscribers, onSave, onDelete, onActiveToggle, currentPage, totalItems, itemsPerPage, onPageChange }: SubscriberTableProps) => {
        const [canEdit, setCanEdit] = useState(false);
        const [editing, setEditing] = useState<{ id?: number | null; value: string }>({
            id: null,
            value: ''
        });

        const handleEditStart = (subscriber: Subscriber) => {
            setEditing({
                id: subscriber.subscriber_id,
                value: subscriber.email
            });
        };

        const handleEditCancel = () => {
            setEditing({
                id: null,
                value: ''
            });
        };

        const handleEditSave = (subscriber: Subscriber) => {
            onSave(subscriber, editing.value);
            handleEditCancel();
        };

        const handleEditChange = (value: string) => {
            setEditing((prev) => ({
                ...prev,
                value
            }));
        };

        const renderEditableCell = (subscriber: Subscriber) => {
            if (editing.id === subscriber.subscriber_id) {
                return (
                    <div className="d-flex align-items-center">
                        <Form.Control
                            type="email"
                            value={editing.value}
                            onChange={(e) => handleEditChange(e.target.value)}
                            className="me-2"
                            autoFocus
                        />
                        <Button
                            variant="success"
                            size="sm"
                            className="me-1"
                            onClick={() => handleEditSave(subscriber)}
                        >
                            <RiCheckLine />
                        </Button>
                        <Button variant="secondary" size="sm" onClick={handleEditCancel}>
                            <RiCloseLine />
                        </Button>
                    </div>
                );
            }

            return (
                <div className="d-flex align-items-center justify-content-between">
                    <span className="text-break">{subscriber.email}</span>
                    {canEdit && (
                        <div>
                            <Button
                                variant="link"
                                size="sm"
                                className="text-muted transition-icon"
                                onClick={() => handleEditStart(subscriber)}
                            >
                                <RiEditLine />
                            </Button>
                            <Button
                                variant="link"
                                size="sm"
                                className="text-muted transition-icon-2"
                                onClick={() => onDelete(subscriber)}
                                disabled={subscriber.active}
                            >
                                <RiDeleteBin2Line />
                            </Button>
                        </div>
                    )}
                </div>
            );
        };

        const renderActiveToggle = (subscriber: Subscriber) => {
            const isDisabled = !canEdit || subscriber.bounced || subscriber.complained;
            return (
                <Form.Check
                    type="switch"
                    checked={subscriber.active}
                    onChange={() => onActiveToggle(subscriber)}
                    disabled={isDisabled}
                    className="d-inline-block"
                />
            );
        };

        const renderDeleteButton = (subscriber: Subscriber) => (
            <OverlayTrigger
                placement="top"
                overlay={
                    <Tooltip>
                        {subscriber.active
                            ? 'Deactivate subscriber before deleting.'
                            : 'Delete subscriber'}
                    </Tooltip>
                }
            >
                <div>
                    <Button
                        variant="danger"
                        size="sm"
                        onClick={() => onDelete(subscriber)}
                        disabled={subscriber.active}
                    >
                        <RiDeleteBin2Line />
                    </Button>
                </div>
            </OverlayTrigger>
        );

        return (
            <div className="modern-subscriber-table">
                <div className="table-header">
                    <h3 className="section-title mb-0">Subscribers</h3>
                    <Button
                        variant={canEdit ? 'outline-secondary' : 'outline-primary'}
                        size="sm"
                        onClick={() => setCanEdit(!canEdit)}
                        className="edit-toggle-btn"
                    >
                        {canEdit ? (
                            <>
                                <RiLockLine /> Lock Editing
                            </>
                        ) : (
                            <>
                                <RiLockUnlockLine /> Enable Editing
                            </>
                        )}
                    </Button>
                </div>
                <div className="table-wrapper">
                    <Table hover responsive className="modern-table">
                    <thead className="bg-light sticky-top">
                        <tr>
                            <th className="d-flex align-items-center">
                                <span>Email</span>
                            </th>
                            <th className="text-center">Status</th>
                            <th className="text-center">Active</th>
                        </tr>
                    </thead>
                    <tbody>
                        {subscribers.map((subscriber) => {
                            const hasError = !subscriber.active && subscriber.note;
                            const rowClass = hasError ? 'table-danger' : '';
                            
                            return (
                                <tr key={subscriber.subscriber_id} className={rowClass}>
                                    <td className="text-break">{renderEditableCell(subscriber)}</td>
                                    <td className="text-center">
                                        {hasError ? (
                                            <OverlayTrigger
                                                placement="top"
                                                overlay={<Tooltip>{subscriber.note}</Tooltip>}
                                            >
                                                <span className="text-danger">
                                                    <RiErrorWarningLine />
                                                </span>
                                            </OverlayTrigger>
                                        ) : (
                                            <span className="text-success">✓</span>
                                        )}
                                    </td>
                                    <td className="text-center p-1 w-25">
                                        {renderActiveToggle(subscriber)}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                    </Table>
                </div>
                {totalItems > itemsPerPage && (
                    <div className="d-flex justify-content-center mt-3">
                        <Pagination>
                            <Pagination.Prev 
                                disabled={currentPage === 1}
                                onClick={() => onPageChange(currentPage - 1)}
                            />
                            {Array.from({ length: Math.ceil(totalItems / itemsPerPage) }, (_, i) => i + 1).map(page => (
                                <Pagination.Item
                                    key={page}
                                    active={page === currentPage}
                                    onClick={() => onPageChange(page)}
                                >
                                    {page}
                                </Pagination.Item>
                            ))}
                            <Pagination.Next 
                                disabled={currentPage === Math.ceil(totalItems / itemsPerPage)}
                                onClick={() => onPageChange(currentPage + 1)}
                            />
                        </Pagination>
                    </div>
                )}
            </div>
        );
    }
);

// Add display name for debugging purposes
SubscriberTable.displayName = 'SubscriberTable';

export default SubscriberTable;
